import { useWindowListener } from '@quizzach/contexts';
import { Colors, H3, TextSize, Text } from '@quizzach/styles';
import { QuizSlidesContainer } from '@quizzach/play-styles';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { QuizOutletContextType, RoutePath } from '@quizzach/interfaces';
import { BottomNav, FitToViewport } from '@quizzach/components';
import { BottomNavMobile } from '@quizzach/layouts';
import { useEffect } from 'react';
import { markHandwrittenWords, scrollToTop } from '@quizzach/utils';
import { motion } from 'framer-motion';
import { popInMotionConfig } from '@quizzach/motion';
import { useTranslation } from 'react-i18next';

export const QuizChapter = () => {
	const { t } = useTranslation();
	const { touchDevice } = useWindowListener();
	const { chapterNumber } = useParams<{ chapterNumber: string }>();
	const location = useLocation();
	const { quiz, currentQuestionNumber, handlePrevious, handleNext } =
		useOutletContext<QuizOutletContextType>();
	const isChapterEnd =
		location.pathname.includes(
			RoutePath.QuizQuestionChapterEnd.replace('/:chapterNumber', '')
		) ||
		location.pathname.includes(
			RoutePath.QuizAnswerChapterEnd.replace('/:chapterNumber', '')
		);
	const isAnswerFlow = location.pathname.includes(
		RoutePath.QuizAnswerChapter.replace('/:chapterNumber', '')
	);

	useEffect(() => {
		scrollToTop();
	}, []);

	// Extract chapter number from params (works for both question-chapter and answer-chapter URLs)
	const chapterIndex = chapterNumber ? parseInt(chapterNumber, 10) - 1 : -1;
	const chapter =
		chapterIndex >= 0 && quiz?.premade_quiz?.chapters
			? quiz.premade_quiz.chapters[chapterIndex]
			: null;

	if (!chapter) {
		return (
			<QuizSlidesContainer className='slides-centered'>
				<div className='quiz-break-content'>
					<div className='quiz-break-main'>
						<Text $size={TextSize.m} color={Colors.textSoft}>
							{t('quiz.chapter.notFound')}
						</Text>
					</div>
				</div>
			</QuizSlidesContainer>
		);
	}

	// The answer flow shows the alternate description when one is provided;
	// the question flow always uses the original description.
	const displayedDescription =
		isAnswerFlow && chapter.alternate_description
			? chapter.alternate_description
			: chapter.description;

	const chapterContent = (
		<motion.div className='quiz-break-content' {...popInMotionConfig()}>
			<div className='quiz-break-main'>
				<H3 color={Colors.textMain}>
					<span className='chapter-end-label'>
						{isChapterEnd ? t('quiz.chapter.end') : t('quiz.chapter.label')}
					</span>
					{chapter.title}
				</H3>

				{!isChapterEnd && displayedDescription && (
					<Text
						as='div'
						className='chapter-description'
						$size={touchDevice ? TextSize.m : TextSize.l}
						color={Colors.textSoft}
						dangerouslySetInnerHTML={{
							__html: markHandwrittenWords(displayedDescription),
						}}
					/>
				)}
			</div>
		</motion.div>
	);

	return (
		<QuizSlidesContainer className='slides-centered'>
			{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
			    desktop/presentation concern. */}
			{touchDevice ? (
				chapterContent
			) : (
				<FitToViewport>{chapterContent}</FitToViewport>
			)}
			{touchDevice ? (
				<BottomNavMobile
					question={quiz?.questions[currentQuestionNumber - 1]}
					handleNext={handleNext}
					handlePrevious={handlePrevious}
					isPremadeOrEstimation={true}
					type='plain'
				/>
			) : (
				<BottomNav
					question={quiz?.questions[currentQuestionNumber - 1]}
					handleNext={handleNext}
					handlePrevious={handlePrevious}
					isPremadeOrEstimation={true}
					type='plain'
				/>
			)}
		</QuizSlidesContainer>
	);
};
